import React, { useState, useEffect } from 'react';
import Button from '../common/Button';
import BeforeAfterImageCard from './BeforeAfterImageCard';
import AddBeforeAfterImageModal from './AddBeforeAfterImageModal';
import ConfirmDialog from '../common/ConfirmDialog';
import { getClinicServices, getBeforeAfterImages, deleteBeforeAfterImage } from '../../services/api';

const BeforeAfterImagesTab = ({ clinicId }) => {
    const [clinicServices, setClinicServices] = useState([]);
    const [selectedService, setSelectedService] = useState(null);
    const [beforeAfterImages, setBeforeAfterImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [imagesLoading, setImagesLoading] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [imageToDelete, setImageToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    useEffect(() => {
        fetchClinicServices();
    }, [clinicId]);

    const fetchClinicServices = async () => {
        setLoading(true);
        try {
            const response = await getClinicServices(clinicId);
            if (response.success) {
                setClinicServices(response.data || []);
            } else {
                console.error('Failed to fetch clinic services:', response.message);
            }
        } catch (error) {
            console.error('Error fetching clinic services:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleServiceSelect = async (service) => {
        setSelectedService(service);
        await fetchBeforeAfterImages(service.id);
    };

    const fetchBeforeAfterImages = async (clinicServiceId) => {
        setImagesLoading(true);
        try {
            const response = await getBeforeAfterImages(clinicServiceId);
            if (response.success) {
                setBeforeAfterImages(response.data || []);
            } else {
                console.error('Failed to fetch before/after images:', response.message);
            }
        } catch (error) {
            console.error('Error fetching before/after images:', error);
        } finally {
            setImagesLoading(false);
        }
    };

    const handleAddImages = () => {
        setShowAddModal(true);
    };

    const handleImagesUploaded = (newImageData) => {
        setBeforeAfterImages(prev => [newImageData, ...prev]);
        setShowAddModal(false);
    };

    const handleDeleteImage = (imageData) => {
        setImageToDelete(imageData);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        if (!imageToDelete) return;

        setDeleteLoading(true);
        try {
            const response = await deleteBeforeAfterImage(imageToDelete.id);
            
            if (response.success) {
                setBeforeAfterImages(prev => prev.filter(img => img.id !== imageToDelete.id));
                setShowDeleteConfirm(false);
                setImageToDelete(null);
            } else {
                console.error('Failed to delete image:', response.message);
            }
        } catch (error) {
            console.error('Error deleting image:', error);
        } finally {
            setDeleteLoading(false);
        }
    };

    const cancelDelete = () => {
        setShowDeleteConfirm(false);
        setImageToDelete(null);
        setDeleteLoading(false);
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(price);
    };

    const formatDuration = (minutes) => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        if (hours > 0) {
            return `${hours}h ${mins > 0 ? `${mins}m` : ''}`.trim();
        }
        return `${mins}m`;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading clinic services...</p>
                </div>
            </div>
        );
    }

    if (!clinicServices || clinicServices.length === 0) {
        return (
            <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                    <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                </div>
                <p className="text-gray-500">No services available for this clinic.</p>
                <p className="text-gray-400 text-sm">Add services to the clinic first to manage before/after images.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Services List */}
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Select a Service</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {clinicServices.map((service) => (
                        <div
                            key={service.id}
                            className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                                selectedService?.id === service.id
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => handleServiceSelect(service)}
                        >
                            <h4 className="font-medium text-gray-900 mb-2">
                                {service.service?.name || 'Unknown Service'}
                            </h4>
                            <div className="space-y-1 text-sm text-gray-600">
                                <div className="flex justify-between">
                                    <span>Price:</span>
                                    <span className="font-medium">{formatPrice(service.basePrice)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Duration:</span>
                                    <span>{formatDuration(service.durationMinutes)}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Selected Service Images */}
            {selectedService && (
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                Before/After Images - {selectedService.service?.name}
                            </h3>
                            <p className="text-sm text-gray-500">
                                {beforeAfterImages.length} image{beforeAfterImages.length !== 1 ? 's' : ''} available
                            </p>
                        </div>
                        <Button
                            type="button"
                            variant="primary"
                            onClick={handleAddImages}
                        >
                            Add New Images
                        </Button>
                    </div>

                    {imagesLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                <p className="text-gray-600">Loading images...</p>
                            </div>
                        </div>
                    ) : beforeAfterImages.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="text-gray-400 mb-4">
                                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <p className="text-gray-500">No before/after images available for this service.</p>
                            <p className="text-gray-400 text-sm">Click "Add New Images" to get started.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {beforeAfterImages.map((imageData) => (
                                <BeforeAfterImageCard
                                    key={imageData.id}
                                    imageData={imageData}
                                    onDelete={handleDeleteImage}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Modals */}
            <AddBeforeAfterImageModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSuccess={handleImagesUploaded}
                clinicServiceId={selectedService?.id}
            />

            <ConfirmDialog
                isOpen={showDeleteConfirm}
                onClose={cancelDelete}
                onConfirm={confirmDelete}
                title="Delete Before/After Images"
                message="Are you sure you want to delete these before/after images? This action cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
                loading={deleteLoading}
                variant="danger"
            />
        </div>
    );
};

export default BeforeAfterImagesTab; 